import { useCallback, useState } from 'react';
import { match } from 'ts-pattern';
import { Modal, useSnackbar } from 'ui-components';
import { FEATURE_FLAGS } from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import {
  AddPlatformResponse,
  useAddPlatform,
} from '~/hooks/api/useAddPlatform';
import { useSearchCreatorPolling } from '~/hooks/api/useSearchCreatorPolling';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useRecentlyAddedTalent } from '~/hooks/useRecentlyAddedTalent';
import { PlatformInfo } from '~/screens/AddTalent/PlatformInfo';
import { ProfileInformation } from '~/screens/AddTalent/ProfileInformation';
import { ConfirmChanges } from '~/screens/MyAgency/components/ConfirmChanges';
import { ICreator } from '~/types';
import { isValidUrl } from '~/utils-ts';

import { SearchHandle } from './SearchHandle';
import { UnknownStep } from './UnknownStep';
import { useAddTalentStore } from './useAddTalentStore';

interface AddTalentProps {
  onSuccess?: (creator: ICreator) => void;
}

export const AddTalent = ({ onSuccess }: AddTalentProps) => {
  const { checkUpdate } = useSearchCreatorPolling();
  const {
    searchResult,
    searchedHandle,
    platforms,
    step,
    isModalOpen,
    reset,
    goToPreviousStep,
  } = useAddTalentStore();

  const { setRecentTalentAddedTimestamp } = useRecentlyAddedTalent();

  const { isEnabled: isFoamAddOnPlatformEnabled } = useFeature(
    FEATURE_FLAGS.foamAddOnPlatform,
  );

  const addPlatform = useAddPlatform();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const onCloseModal = useCallback(() => {
    if (step >= 1 && !addPlatform.isLoading) {
      setIsConfirmationModalOpen(true);
      return;
    }

    reset();
  }, [step, addPlatform.isLoading, reset]);

  const keepEditing = useCallback(() => {
    setIsConfirmationModalOpen(false);
  }, [setIsConfirmationModalOpen]);

  const leavePage = useCallback(() => {
    setIsConfirmationModalOpen(false);
    reset();
  }, [reset, setIsConfirmationModalOpen]);

  const snackbar = useSnackbar();

  const handleAddTalentSuccess = useCallback(
    async (resp: AddPlatformResponse) => {
      const isUpdated = await checkUpdate(resp.data.id);

      setRecentTalentAddedTimestamp(); // set successfully added talent timestamp to local storage, to handle TTA processing notification

      // FOAM-13281: track event if the handle is an URL
      if (searchedHandle && isValidUrl(searchedHandle!)) {
        trackEvent('foam-add-single-talent-by-url');
      }

      if (isUpdated && isModalOpen) {
        snackbar.success({
          description: 'Talent successfully added',
        });
        reset();
        onSuccess?.(resp.data);
      }
    },
    [
      checkUpdate,
      snackbar,
      reset,
      isModalOpen,
      onSuccess,
      setRecentTalentAddedTimestamp,
    ],
  );

  const getCreatorInfo = useCallback(() => {
    // Determines and returns creator information based on available data sources.
    // Priority 1: Use searchResult if it has a profile picture
    if (searchResult?.profilePictureUrl) {
      return {
        avatar: searchResult.profilePictureUrl,
        firstName: searchResult.name?.split(' ').at(0) || '',
        lastName: searchResult.name?.split(' ').at(1) || '',
      };
    }

    // Priority 2: If no platforms available, use searchResult name with empty avatar
    if (!platforms?.length) {
      return {
        avatar: '',
        firstName: searchResult?.name?.split(' ').at(0) || '',
        lastName: searchResult?.name?.split(' ').at(1) || '',
      };
    }

    // Priority 3: Find first platform with a profile picture
    const platformWithPicture = platforms.find(
      (platform) => platform?.profilePictureUrl,
    );

    // Priority 4: Fallback to first platform if no platform has a picture
    const platformToUse = platformWithPicture || platforms[0];

    return {
      avatar: platformToUse?.profilePictureUrl,
      firstName: platformToUse?.name?.split(' ').at(0) || '',
      lastName: platformToUse?.name?.split(' ').at(1) || '',
    };
  }, [searchResult, platforms]);
  return (
    <Modal
      disableModalContent
      isOpen={isModalOpen}
      onClose={onCloseModal}
      onEsc={onCloseModal}
      size={step === 2 ? 'lg' : 'md'}
    >
      {match(step)
        .with(0, () => (
          <SearchHandle searchOnPlatform={isFoamAddOnPlatformEnabled} />
        ))
        .with(1, () => <PlatformInfo />)
        .with(2, () => (
          <ProfileInformation
            onClose={goToPreviousStep}
            onUpdate={handleAddTalentSuccess}
            creator={getCreatorInfo()}
          />
        ))
        .otherwise(() => (
          <UnknownStep />
        ))}
      <ConfirmChanges
        hasUnsavedChanges={isConfirmationModalOpen}
        keepEditing={keepEditing}
        leavePage={leavePage}
      />
    </Modal>
  );
};
